import { t } from 'i18next';
import React, { useEffect, useRef } from 'react';
import styles from '../styles/ptContactForm.scss';
import { Grid, TextField, Button, DriftSnackbar } from 'drift-ui';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { getUser } from '../../../redux/actions/appActions';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSendContactUsFormMutation } from '../../../state/paymentService';

const contactFormSchema = z.object({
  firstName: z.string().min(1, { message: t('Required field') }),
  lastName: z.string().min(1, { message: t('Required field') }),
  companyName: z.string().min(1, { message: t('Required field') }),
  phoneNumber: z
    .string()
    .min(1, { message: t('Required field') })
    .regex(/^\d{10,11}$/, { message: t('Invalid phone number') }),
  email: z
    .string()
    .email()
    .min(1, { message: t('Required field') }),
  subscriptionName: z.enum(['Legacy', 'Enterprise'], {
    errorMap: () => ({ message: t('Required field') }),
  }),
});

interface ContactFormProps {
  subscriptionName: 'Legacy' | 'Enterprise';
}

export default function ContactForm({ subscriptionName }: ContactFormProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const user = useSelector(getUser, shallowEqual);
  const [sendContactUsForm] = useSendContactUsFormMutation();
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitSuccessful, isSubmitting },
  } = useForm({
    resolver: zodResolver(contactFormSchema),
    mode: 'onTouched',
    delayError: 400,
    defaultValues: {
      firstName: user?.firstname ?? '',
      lastName: user?.lastname ?? '',
      companyName: user?.company ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phone ?? '',
      subscriptionName,
    },
  });

  const onSubmit = async (data) => {
    try {
      await sendContactUsForm(data).unwrap();
    } catch (error) {
      console.error('Failed to send contact form:', error);
      throw error;
    } finally {
      setShowSnackbar(true);
    }
  };

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <div ref={ref} className={styles.contactFormContainer}>
      <h2 className={styles.contactUsHeader}>{t('Contact us about upgrading your ProTransport Plan')}</h2>
      <h4 className={styles.contactUsSubHeader}>
        {t(
          'Submitting your request will contact one of our representatives to reach out to you with more information.',
        )}
      </h4>
      <div className={styles.contactForm}>
        <Grid container columnSpacing={8} rowSpacing={6}>
          <Grid size={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('First Name')}
                  placeholder={t('First Name')}
                  type="text"
                  helperText={errors?.firstName?.message}
                  error={!!errors?.firstName}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('Last Name')}
                  placeholder={t('Last Name')}
                  type="text"
                  helperText={errors?.lastName?.message}
                  error={!!errors?.lastName}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="companyName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('Company Name')}
                  placeholder={t('Company Name')}
                  type="text"
                  helperText={errors?.companyName?.message}
                  error={!!errors?.companyName}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('Email')}
                  placeholder={t('Email')}
                  type="text"
                  helperText={errors?.email?.message}
                  error={!!errors?.email}
                />
              )}
            />
          </Grid>
          <Grid size={6}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label={t('Phone')}
                  placeholder={t('Phone')}
                  type="text"
                  helperText={errors?.phoneNumber?.message}
                  error={!!errors?.phoneNumber}
                />
              )}
            />
          </Grid>
          <Grid size={6} />
          <Grid size={6} />
          <Grid className={styles.submitButtonContainer} size={6}>
            <Button
              className={styles.submitButton}
              color="primary"
              variant="contained"
              size="medium"
              disabled={!isValid}
              loading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {t('Submit request')}
            </Button>
          </Grid>
        </Grid>
      </div>
      <DriftSnackbar
        open={showSnackbar}
        message={
          isSubmitSuccessful
            ? t('Form submitted successfully!')
            : t('There was an error submitting the form. Please try again.')
        }
        variant={isSubmitSuccessful ? 'success' : 'error'}
        onClose={() => {
          setShowSnackbar(false);
        }}
      />
    </div>
  );
}
